<script setup lang="ts">
import { ref } from "vue";
import { UiText, UiTabs, UiTabsItem, UiIcon, UiButton, UiAside } from "@shopexperts/shared";
import { useClientReviewRequests } from "@/app/composables/reviews/useClientReviewRequests";
import { useClientReviews } from "@/app/composables/reviews/useClientReviews";
import ClientReviewRequestCard from "@/app/components/reviews/ClientReviewRequestCard.vue";
import ClientReviewCard from "@/app/components/reviews/ClientReviewCard.vue";
import PageHeader from "@/app/components/common/PageHeader.vue";
import EditReviewForm from "@/app/components/reviews/EditReviewForm.vue";
import type { ClientReviewSchema } from "@shopexperts/shared";
import { z } from "zod";

type ClientReview = z.infer<typeof ClientReviewSchema>;

// Tab state
const activeTab = ref<'review_requests' | 'my_reviews'>('review_requests');

// Status filter for review requests
const reviewRequestStatus = ref<'pending' | 'completed' | 'expired' | undefined>(undefined);

// Fetch review requests
const { 
  reviewRequests, 
  isLoading: isLoadingRequests, 
  error: requestsError, 
  refetch: refetchRequests 
} = useClientReviewRequests(reviewRequestStatus);

// Fetch submitted reviews
const { 
  reviews, 
  isLoading: isLoadingReviews, 
  error: reviewsError 
} = useClientReviews();

const handleReviewSubmitted = () => {
  refetchRequests();
};

// Edit review state
const isEditAsideOpen = ref(false);
const selectedReview = ref<ClientReview | null>(null);

const handleEditReview = (review: ClientReview) => {
  selectedReview.value = review;
  isEditAsideOpen.value = true;
};

const handleEditSuccess = () => {
  isEditAsideOpen.value = false;
  selectedReview.value = null;
};

const handleEditCancel = () => {
  isEditAsideOpen.value = false;
  selectedReview.value = null;
};
</script>

<template>
  <main class="flex-1 p-4 md:p-8 overflow-y-auto bg-secondary font-light w-full">
    <PageHeader
      title="Reviews"
      description="Manage review requests and view your submitted reviews"
    />

    <!-- Tabs -->
    <UiTabs v-model="activeTab" default-value="review_requests">
      <!-- Review Requests Tab -->
      <UiTabsItem value="review_requests" label="Review Requests">
        <!-- Loading State -->
        <div v-if="isLoadingRequests" class="text-center py-12">
          <div class="flex flex-col items-center gap-3">
            <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <UiText variant="p" as="p" class="text-gray-500">Loading review requests...</UiText>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="requestsError" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load review requests
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600 mb-4">
              {{ (requestsError as Error).message || 'An error occurred while fetching review requests' }}
            </UiText>
            <UiButton variant="outline" label="Try Again" @click="refetchRequests" />
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="reviewRequests.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4 max-w-md mx-auto">
            <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
              <UiIcon name="star" class="w-10 h-10 text-gray-400" />
            </div>
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-2">
                No review requests yet
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                When experts request reviews from you, they'll appear here.
              </UiText>
            </div>
          </div>
        </div>

        <!-- Review Requests List -->
        <div v-else class="flex flex-col gap-4">
          <ClientReviewRequestCard
            v-for="request in reviewRequests"
            :key="request.id"
            :request="request"
            @review-submitted="handleReviewSubmitted"
          />
        </div>
      </UiTabsItem>

      <!-- My Reviews Tab -->
      <UiTabsItem value="my_reviews" label="My Reviews">
        <!-- Loading State -->
        <div v-if="isLoadingReviews" class="text-center py-12">
          <div class="flex flex-col items-center gap-3">
            <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <UiText variant="p" as="p" class="text-gray-500">Loading your reviews...</UiText>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="reviewsError" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load reviews
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600">
              {{ (reviewsError as Error).message || 'An error occurred while fetching your reviews' }}
            </UiText>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="reviews.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4 max-w-md mx-auto">
            <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
              <UiIcon name="pencil" class="w-10 h-10 text-gray-400" />
            </div>
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-2">
                No reviews submitted yet
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                After you submit reviews for experts, they'll appear here.
              </UiText>
            </div>
          </div>
        </div>

        <!-- Reviews List -->
        <div v-else class="flex flex-col gap-4">
          <ClientReviewCard
            v-for="review in reviews"
            :key="review.id"
            :review="review"
            @edit="handleEditReview"
          />
        </div>
      </UiTabsItem>
    </UiTabs>

    <!-- Edit Review Aside -->
    <UiAside v-model="isEditAsideOpen" position="right" :close-on-overlay-click="false">
      <div class="p-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <EditReviewForm
          v-if="selectedReview"
          :key="selectedReview.id"
          :review-id="selectedReview.id"
          :expert="{
            name: selectedReview.expert_name,
            type: selectedReview.expert_type,
            slug: selectedReview.expert_slug,
            profile_picture: selectedReview.expert_profile_picture,
            role: selectedReview.project_name
          }"
          :initial-values="{
            quality: selectedReview.quality,
            communication: selectedReview.communication,
            timelines: selectedReview.timelines,
            value_for_money: selectedReview.value_for_money,
            is_anonymous: selectedReview.is_anonymous,
            review_text: selectedReview.review_text
          }"
          @success="handleEditSuccess"
          @cancel="handleEditCancel"
        />
      </div>
    </UiAside>
  </main>
</template>

